export const formatPrice = (price: number | string, symbol: string): string => {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  // Determine asset type and decimal places based on symbol and price
  const { decimalPlaces, currency } = getAssetFormatting(symbol, numPrice);
  
  // Format price with appropriate decimal places using US locale
  const formatted = numPrice.toLocaleString('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  });
  
  // Add currency symbol/abbreviation
  if (currency === 'USD') {
    return `$${formatted}`;
  } else if (currency === 'EUR') {
    return `€${formatted}`;
  } else if (currency === 'GBP') {
    return `£${formatted}`;
  } else if (currency === 'JPY') {
    return `¥${formatted}`;
  }
  
  return `${formatted} ${currency}`;
};

export const formatPriceWithoutCurrency = (price: number | string, symbol: string): string => {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  // Determine asset type and decimal places based on symbol and price
  const { decimalPlaces } = getAssetFormatting(symbol, numPrice);
  
  // Format price with appropriate decimal places using US locale
  return numPrice.toLocaleString('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  });
};

/**
 * Get appropriate decimal places for an asset based on its typical price range
 * Maximum decimal places is capped at 5
 * @param symbol - Asset symbol (e.g., 'BTCUSDT', 'ETHUSDT', 'EURUSD')
 * @param price - Current price (optional, used for dynamic calculation)
 * @returns Number of decimal places to display (max 5)
 */
export const getAssetDecimalPlaces = (symbol: string, price?: number): number => {
  // If price is provided, calculate dynamically based on price magnitude
  if (price !== undefined) {
    if (price >= 10000) return 2;       // Very high value assets (BTC, ETH, etc.)
    if (price >= 1000) return 2;        // High value assets (stocks, indices)
    if (price >= 100) return 2;         // Medium-high value assets
    if (price >= 10) return 3;          // Medium value assets
    if (price >= 1) return 4;           // Low-medium value assets
    if (price >= 0.1) return 4;         // Low value assets
    if (price >= 0.01) return 5;        // Very low value assets (capped at 5)
    if (price >= 0.001) return 5;       // Extremely low value assets (capped at 5)
    return 5;                            // Minimum value assets (capped at 5)
  }
  
  // Fallback logic based on symbol patterns when price is not available
  const cleanSymbol = symbol.replace(/-.*$/, '').replace(/USDT$/, '');
  
  // Crypto assets with high values (BTC, ETH, SOL, etc.)
  if (cleanSymbol.includes('BTC') || cleanSymbol.includes('ETH') || cleanSymbol.includes('SOL')) {
    return 2; // e.g., 45,234.56
  }
  
  // Crypto assets with medium values (ADA, DOT, LINK, etc.)
  if (cleanSymbol.includes('ADA') || cleanSymbol.includes('DOT') || cleanSymbol.includes('LINK') || 
      cleanSymbol.includes('XRP') || cleanSymbol.includes('LTC') || cleanSymbol.includes('BCH')) {
    return 4; // e.g., 0.4523
  }
  
  // Crypto assets with low values (DOGE, XLM, etc.)
  if (cleanSymbol.includes('DOGE') || cleanSymbol.includes('XLM') || cleanSymbol.includes('XMR')) {
    return 5; // e.g., 0.07890 (capped at 5)
  }
  
  // Forex pairs
  if (cleanSymbol.includes('USD') || cleanSymbol.includes('EUR') || cleanSymbol.includes('GBP') || 
      cleanSymbol.includes('JPY') || cleanSymbol.includes('AUD') || cleanSymbol.includes('CAD') || 
      cleanSymbol.includes('CHF') || cleanSymbol.includes('NZD')) {
    return 5; // e.g., 1.08925 (capped at 5)
  }
  
  // Stocks and indices
  if (cleanSymbol.includes('SPX') || cleanSymbol.includes('NASDAQ') || cleanSymbol.includes('DJI') ||
      cleanSymbol.includes('AAPL') || cleanSymbol.includes('MSFT') || cleanSymbol.includes('GOOGL')) {
    return 2; // e.g., 4,567.89
  }
  
  // Commodities
  if (cleanSymbol.includes('GOLD') || cleanSymbol.includes('SILVER') || cleanSymbol.includes('OIL')) {
    return 2; // e.g., 2,034.56
  }
  
  // Default fallback
  return 4;
};

/**
 * Format price with appropriate decimal places for the asset
 * Maximum decimal places is capped at 5
 * @param price - Price to format
 * @param symbol - Asset symbol
 * @returns Formatted price string
 */
export const formatPriceWithAssetDecimals = (price: string | number, symbol: string): string => {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  const decimals = getAssetDecimalPlaces(symbol, numPrice);
  
  // Handle very small numbers
  if (numPrice < 0.000001) {
    return numPrice.toExponential(4);
  }
  
  // Format with appropriate decimals
  const formatted = numPrice.toFixed(decimals);
  
  // Remove trailing zeros for cleaner display
  return formatted.replace(/\.?0+$/, '');
};

/**
 * Format price change with appropriate decimal places
 * Maximum decimal places is capped at 5
 * @param change - Price change value
 * @param symbol - Asset symbol
 * @returns Formatted change string
 */
export const formatPriceChange = (change: number, symbol: string): string => {
  const decimals = getAssetDecimalPlaces(symbol, Math.abs(change));
  const formatted = change.toFixed(decimals);
  
  // Add sign and percentage
  const sign = change >= 0 ? '+' : '';
  return `${sign}${formatted}`;
};

// Helper function to determine decimal places and currency based on asset type
function getAssetFormatting(symbol: string, price: number): { decimalPlaces: number; currency: string } {
  // Remove any suffixes like -PERP, -USDT, etc.
  const cleanSymbol = symbol.replace(/-.*$/, '').replace(/USDT$/, '');
  
  // Calculate decimal places based on price magnitude, capped at 5
  let decimalPlaces = 2; // Default
  
  if (price >= 10000) {
    decimalPlaces = 2;       // Very high value assets (BTC, ETH, etc.)
  } else if (price >= 1000) {
    decimalPlaces = 2;       // High value assets (stocks, indices)
  } else if (price >= 100) {
    decimalPlaces = 2;       // Medium-high value assets
  } else if (price >= 10) {
    decimalPlaces = 3;       // Medium value assets
  } else if (price >= 1) {
    decimalPlaces = 4;       // Low-medium value assets
  } else if (price >= 0.1) {
    decimalPlaces = 4;       // Low value assets
  } else if (price >= 0.01) {
    decimalPlaces = 5;       // Very low value assets (capped at 5)
  } else if (price >= 0.001) {
    decimalPlaces = 5;       // Extremely low value assets (capped at 5)
  } else {
    decimalPlaces = 5;       // Minimum value assets (capped at 5)
  }
  
  // Ensure decimal places never exceed 5
  decimalPlaces = Math.min(decimalPlaces, 5);
  
  // Determine currency based on asset type
  let currency = 'USD'; // Default
  
  // Forex pairs (major and minor)
  if (isForexSymbol(cleanSymbol)) {
    currency = 'USD';
  }
  // Cryptocurrencies
  else if (isCryptoSymbol(cleanSymbol)) {
    currency = 'USD';
  }
  // Stocks (US stocks typically)
  else if (isStockSymbol(cleanSymbol)) {
    currency = 'USD';
  }
  // Commodities
  else if (isCommoditySymbol(cleanSymbol)) {
    currency = 'USD';
  }
  
  return { decimalPlaces, currency };
}

// Helper functions to identify asset types
function isForexSymbol(symbol: string): boolean {
  const forexPairs = [
    'EURUSD', 'GBPUSD', 'USDJPY', 'USDCHF', 'AUDUSD', 'USDCAD', 'NZDUSD',
    'EURGBP', 'EURJPY', 'GBPJPY', 'AUDCAD', 'AUDCHF', 'AUDJPY', 'AUDNZD',
    'CADCHF', 'CADJPY', 'CHFJPY', 'EURAUD', 'EURCAD', 'EURNZD', 'GBPAUD',
    'GBPCAD', 'GBPCHF', 'GBPNZD', 'NZDCAD', 'NZDCHF', 'NZDJPY'
  ];
  return forexPairs.includes(symbol);
}

function isCryptoSymbol(symbol: string): boolean {
  const cryptoKeywords = ['BTC', 'ETH', 'ADA', 'DOT', 'LINK', 'LTC', 'XRP', 'BCH', 'EOS', 'XLM', 'SOL', 'AVAX', 'MATIC', 'UNI', 'DOGE', 'XMR'];
  return cryptoKeywords.some(keyword => symbol.includes(keyword));
}

function isStockSymbol(symbol: string): boolean {
  // US stocks typically don't have specific patterns, but we can check for common ones
  const stockKeywords = ['AAPL', 'GOOGL', 'MSFT', 'AMZN', 'TSLA', 'META', 'NVDA', 'NFLX', 'BRK'];
  return stockKeywords.includes(symbol);
}

function isCommoditySymbol(symbol: string): boolean {
  const commodityKeywords = ['GOLD', 'SILVER', 'OIL', 'COPPER', 'NATURAL_GAS', 'PLATINUM', 'CL', 'GC', 'SI', 'HG', 'ZC', 'ZS', 'ZW'];
  return commodityKeywords.some(keyword => symbol.includes(keyword));
}

/**
 * Test function to demonstrate decimal precision logic
 * This function shows how different asset prices get different decimal precision
 * Maximum decimal places is always capped at 5
 */
export const testDecimalPrecision = (): void => {
  // Test high-value crypto (BTC, ETH)
  getAssetDecimalPlaces('BTCUSDT', 45234.56); // Should return 2
  getAssetDecimalPlaces('ETHUSDT', 3234.56);   // Should return 2
  
  // Test medium-value crypto (ADA, DOT)
  getAssetDecimalPlaces('ADAUSDT', 0.4523);     // Should return 4
  getAssetDecimalPlaces('DOTUSDT', 7.89);         // Should return 3
  getAssetDecimalPlaces('TAOUSDT', 0.000001);     // Should return 5
  // Test low-value crypto (DOGE, XLM)
  getAssetDecimalPlaces('DOGEUSDT', 0.07890); // Should return 5 (capped)
  getAssetDecimalPlaces('XLMUSDT', 0.000123); // Should return 5 (capped)
  
  // Test forex pairs
  getAssetDecimalPlaces('EURUSD', 1.08925);      // Should return 5
  getAssetDecimalPlaces('GBPUSD', 1.2700);        // Should return 4
  
  // Test stocks
  getAssetDecimalPlaces('AAPL', 178.45);           // Should return 2
  getAssetDecimalPlaces('TSLA', 234.56);           // Should return 2
  
  // Test commodities
  getAssetDecimalPlaces('GOLD', 2034.56);        // Should return 2
  getAssetDecimalPlaces('OIL', 78.45);               // Should return 2
  
  // Test edge cases
  getAssetDecimalPlaces('TEST', 0.000001); // Should return 5 (capped)
  getAssetDecimalPlaces('TEST', 100000);      // Should return 2
};
